import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { BLOOM_FEATURES, ENTERPRISE_FEATURES, PRICING_FAQ, TRUST_BAR_ITEMS } from '../data/pricingContent';
import '../pages/Pricing.css';

const BLOOM_FEATURE_LEAD = new Set(['Early Access to New Features', 'Verified Founding Member Badge']);

function FAQItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`faq-item ${open ? 'faq-open' : ''}`}>
      <button type="button" className="faq-question" onClick={() => setOpen(!open)}>
        <span>{q}</span>
        <span className="faq-chevron">{open ? '−' : '+'}</span>
      </button>
      {open && <p className="faq-answer">{a}</p>}
    </div>
  );
}

/**
 * Shared pricing block: Bloom + Enterprise. Used on /pricing (full) and Landing #pricing (embedded).
 */
export default function PricingPlansCore({
  embedded = false,
  showTrustBar = true,
  showFaq = false,
  showPricingLink = false,
  bloomWaitlistSource = 'pricing-bloom',
}) {
  const navigate = useNavigate();
  const [billing, setBilling] = useState('monthly');

  const wrapperClass = embedded ? 'pricing-page pricing-page--embed' : 'pricing-page';

  return (
    <div className={wrapperClass}>
      <p className="pricing-eyebrow">PRICING</p>
      <h2 className="serif pricing-headline">
        One platform.
        <br />
        <em style={{ fontStyle: 'italic', color: 'var(--pink)' }}>Every chapter.</em>
      </h2>
      <p className="pricing-subtitle">
        One subscription covers fertility through motherhood. Nova included — but the intelligence is yours either way.
        Enterprise plans for clinics, employers, and health systems opening in 2027.
      </p>

      {/* Restore at launch when paid subscriptions go live:
      <div className="billing-toggle">
        <button
          type="button"
          className={billing === 'monthly' ? 'toggle-active' : 'toggle-inactive'}
          onClick={() => setBilling('monthly')}
        >
          Monthly
        </button>
        <button
          type="button"
          className={billing === 'annual' ? 'toggle-active' : 'toggle-inactive'}
          onClick={() => setBilling('annual')}
        >
          Annual
          <span className="save-badge">Save 21%</span>
        </button>
      </div>
      */}

      <div className="pricing-toggle-cta">
        <Link to="/signup?source=pricing" className="btn-primary btn-pricing-cta">
          Join the waitlist
        </Link>
      </div>

      <div className="pricing-grid pricing-grid--two">
        <div className="plan-card plan-bloom plan-featured">
          <div className="plan-header">
            <div className="plan-name serif">Bloom</div>
            <div className="bloom-founding-badge">🔒 Founding Member Rate</div>
            <div className="bloom-was-price-row">
              <span className="bloom-was-price">$27</span>
              <span className="bloom-was-label">regular price</span>
            </div>
            <div className="plan-price-row">
              <span className="plan-price">{billing === 'annual' ? '$15' : '$19'}</span>
              <span className="plan-price-sub">/per month</span>
            </div>
            <p className="bloom-price-launch-line">
              <strong className="bloom-lock-text">Join the waitlist for a lifetime discount + founding member perks.</strong>{' '}
              Price rises to $27 at launch.
            </p>
            {billing === 'annual' && <p className="plan-annual-note">Billed as $180/year</p>}
          </div>

          <div className="plan-divider" />
          <ul className="plan-features">
            {BLOOM_FEATURES.map((f, i) => (
              <li key={i} className={BLOOM_FEATURE_LEAD.has(f) ? 'plan-feature-bloom-lead' : undefined}>
                <span className="check check-bloom">✓</span>
                <span className={BLOOM_FEATURE_LEAD.has(f) ? 'plan-feature-bloom-lead-text' : undefined}>{f}</span>
              </li>
            ))}
          </ul>

          <button
            type="button"
            className="btn-bloom"
            onClick={() => navigate(`/signup?source=${encodeURIComponent(bloomWaitlistSource)}`)}
          >
            Join the waitlist
          </button>
          <p className="plan-note">Cancel anytime. No data selling. Ever.</p>
        </div>

        <div className="plan-card plan-enterprise">
          <div className="plan-header">
            <div className="plan-name serif">Enterprise</div>
            <div className="plan-price-row">
              <span className="plan-price plan-price-custom">Custom</span>
            </div>
            <p className="plan-desc">
              Clinical partnerships, employer benefits, and health system integrations. Coming 2027.
            </p>
          </div>

          <div className="plan-divider" />
          <ul className="plan-features">
            {ENTERPRISE_FEATURES.map((f, i) => (
              <li key={i}>
                <span className="check">✓</span>
                {f}
              </li>
            ))}
          </ul>

          <button type="button" className="btn-coming-soon" disabled>
            Coming soon
          </button>
          <p className="plan-note">We&apos;ll reach out when partnerships open in 2027.</p>
        </div>
      </div>

      {showTrustBar && (
        <div className="trust-bar">
          {TRUST_BAR_ITEMS.map((t, i) => (
            <span key={i} className="trust-item">
              {t}
            </span>
          ))}
        </div>
      )}

      {showFaq && (
        <div className="pricing-faq">
          <h3 className="serif">Common questions</h3>
          <div className="faq-grid">
            {PRICING_FAQ.map((item, i) => (
              <FAQItem key={i} q={item.q} a={item.a} />
            ))}
          </div>
        </div>
      )}

      {showPricingLink && (
        <p style={{ textAlign: 'center', marginTop: 20, marginBottom: 0 }}>
          <button
            type="button"
            onClick={() => navigate('/pricing')}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontFamily: 'inherit',
              fontSize: 14,
              fontWeight: 600,
              color: '#D4537E',
              textDecoration: 'underline',
              textUnderlineOffset: 3,
            }}
          >
            Full pricing & FAQs →
          </button>
        </p>
      )}
    </div>
  );
}
